/**
 * Hook: useWhatsAppConnection
 *
 * Gestisce la connessione WhatsApp Web tramite le API backend.
 * Include: stato connessione, QR code, connect/disconnect.
 *
 * @example
 * ```tsx
 * function ConnectionStatus() {
 *   const { status, connected, qrCode, connect, disconnect } = useWhatsAppConnection();
 *
 *   return (
 *     <div>
 *       <p>Status: {status}</p>
 *       {qrCode && <img src={qrCode} alt="QR Code" />}
 *       {!connected ? (
 *         <button onClick={connect}>Connetti</button>
 *       ) : (
 *         <button onClick={disconnect}>Disconnetti</button>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */

import { useState, useEffect, useCallback } from 'react';
import { useWhatsAppConfig } from '../WhatsAppProvider';
import { waAuthHeaders } from '../waFetch';

/**
 * Stato della connessione WhatsApp
 */
export interface ConnectionStatus {
  /** Stato testuale: 'connected' | 'disconnected' | 'connecting' | 'qr_ready' */
  status: string;
  /** True se connesso a WhatsApp */
  connected: boolean;
  /** QR code come data URL (se in attesa di scan) */
  qrCode?: string;
  /** Numero telefono connesso */
  phoneNumber?: string;
}

/**
 * Risultato dell'hook useWhatsAppConnection
 */
export interface UseWhatsAppConnectionResult {
  /** Stato corrente */
  status: string;
  /** Se connesso */
  connected: boolean;
  /** QR code data URL */
  qrCode?: string;
  /** Numero telefono */
  phoneNumber?: string;
  /** Operazione in corso */
  loading: boolean;
  /** Errore se presente */
  error: string | null;
  /** Avvia connessione */
  connect: () => Promise<void>;
  /** Disconnetti */
  disconnect: () => Promise<void>;
  /** Riavvia bot */
  restart: () => Promise<void>;
  /** Aggiorna stato */
  refresh: () => Promise<void>;
  /** Fetch QR code */
  fetchQRCode: () => Promise<void>;
}

/**
 * Hook per gestire la connessione WhatsApp Web.
 *
 * Fornisce:
 * - Stato connessione in tempo reale
 * - QR code per l'autenticazione
 * - Metodi connect/disconnect/restart
 * - Polling automatico durante connessione
 *
 * @returns Oggetto con stato e metodi per gestire la connessione
 */
export function useWhatsAppConnection(): UseWhatsAppConnectionResult {
  const { apiBaseUrl } = useWhatsAppConfig();

  const [status, setStatus] = useState<ConnectionStatus>({
    status: 'disconnected',
    connected: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch status corrente dal server
   */
  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${apiBaseUrl}/whatsapp/status`, { headers: waAuthHeaders() });
      const data = await res.json();

      if (data.success) {
        setStatus({
          status: data.status,
          connected: data.connected,
          qrCode: data.qrCode,
          phoneNumber: data.phoneNumber
        });
      }
    } catch (err) {
      console.error('Errore fetch status:', err);
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    }
  }, [apiBaseUrl]);

  /**
   * Avvia connessione a WhatsApp
   */
  const connect = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${apiBaseUrl}/whatsapp/connect`, { method: 'POST', headers: waAuthHeaders() });
      const data = await res.json();

      if (!data.success) {
        throw new Error(data.error || 'Connessione fallita');
      }

      // Poll status ogni 2 secondi per aggiornamenti
      const interval = setInterval(fetchStatus, 2000);

      // Cleanup dopo 60 secondi
      setTimeout(() => clearInterval(interval), 60000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore connessione');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, fetchStatus]);

  /**
   * Disconnetti da WhatsApp
   */
  const disconnect = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${apiBaseUrl}/whatsapp/disconnect`, { method: 'POST', headers: waAuthHeaders() });
      const data = await res.json();

      if (!data.success) {
        throw new Error(data.error || 'Disconnessione fallita');
      }

      await fetchStatus();

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore disconnessione');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, fetchStatus]);

  /**
   * Riavvia il bot WhatsApp
   */
  const restart = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${apiBaseUrl}/whatsapp/restart`, { method: 'POST', headers: waAuthHeaders() });
      const data = await res.json();

      if (!data.success) {
        throw new Error(data.error || 'Riavvio fallito');
      }

      // Poll status
      const interval = setInterval(fetchStatus, 2000);
      setTimeout(() => clearInterval(interval), 60000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore riavvio');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, fetchStatus]);

  /**
   * Fetch QR code separatamente
   */
  const fetchQRCode = useCallback(async () => {
    try {
      const res = await fetch(`${apiBaseUrl}/whatsapp/qr`, { headers: waAuthHeaders() });
      const data = await res.json();

      if (data.success && data.qrCode) {
        setStatus(prev => ({ ...prev, qrCode: data.qrCode }));
      }
    } catch (err) {
      console.error('Errore fetch QR:', err);
    }
  }, [apiBaseUrl]);

  // Fetch status iniziale + polling continuo per rilevare auto-recovery
  useEffect(() => {
    fetchStatus();

    // Polling ogni 10 secondi per rilevare cambiamenti di stato
    // (es. dopo auto-cleanup sessione corrotta → nuovo QR pronto)
    const pollInterval = setInterval(() => {
      fetchStatus();
    }, 10000); // 10 secondi

    return () => clearInterval(pollInterval);
  }, [fetchStatus]);

  return {
    status: status.status,
    connected: status.connected,
    qrCode: status.qrCode,
    phoneNumber: status.phoneNumber,
    loading,
    error,
    connect,
    disconnect,
    restart,
    refresh: fetchStatus,
    fetchQRCode
  };
}
